import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { SVRParamsProps } from './types';
import { KERNEL_TYPES } from './constants';
import InfoTooltip from '@/shared/components/forms/InfoTooltip';

const inputBase = 'w-full border rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring border-input [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

/**
 * SVR Parameters sub-component
 * Displays configuration options for SVR_dir and SVR_MIMO models
 */
export const SVRParams: React.FC<SVRParamsProps> = memo(({
  modelParams,
  disabled,
  onParameterChange
}) => {
  const { t } = useTranslation('training');

  return (
    <>
      {/* Kernel Type */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1">
          <label
            htmlFor="model-config-KERNEL"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.kernelType')}
          </label>
          <InfoTooltip text={modelParams.MODE === 'SVR_MIMO' ? t('tooltips.models.svrMimo.kernel') : t('tooltips.models.svrDir.kernel')} width="w-80" />
        </div>
        <select
          id="model-config-KERNEL"
          value={modelParams.KERNEL || ''}
          onChange={(e) => onParameterChange('KERNEL', e.target.value)}
          disabled={disabled}
          className="w-full border border-input rounded-lg px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm text-foreground bg-card disabled:bg-muted disabled:text-muted-foreground transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:border-ring"
        >
          <option value="">{t('training.modelConfiguration.placeholders.selectKernel')}</option>
          {KERNEL_TYPES.map((kernel) => (
            <option key={kernel} value={kernel}>
              {kernel}
            </option>
          ))}
        </select>
      </div>

      {/* C Parameter */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1">
          <label
            htmlFor="model-config-C"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.cParameter')}
          </label>
          <InfoTooltip text={modelParams.MODE === 'SVR_MIMO' ? t('tooltips.models.svrMimo.c') : t('tooltips.models.svrDir.c')} width="w-80" />
        </div>
        <input
          id="model-config-C"
          type="number"
          value={modelParams.C || ''}
          onChange={(e) => {
            const val = e.target.value;
            onParameterChange('C', val ? parseFloat(val) : undefined);
          }}
          disabled={disabled}
          className={inputBase}
          min="0.001"
          max="1000"
          step="0.1"
          placeholder={t('training.modelConfiguration.placeholders.enterValue')}
        />
      </div>

      {/* Epsilon Parameter */}
      <div className="grid grid-cols-[2fr_3fr] sm:grid-cols-[1fr_2fr] items-center gap-x-3 px-4 py-3 sm:px-6 sm:py-3.5">
        <div className="flex items-center gap-1">
          <label
            htmlFor="model-config-EPSILON"
            className="text-[10px] sm:text-xs uppercase tracking-wide font-medium text-muted-foreground"
          >
            {t('training.modelConfiguration.epsilon')}
          </label>
          <InfoTooltip text={modelParams.MODE === 'SVR_MIMO' ? t('tooltips.models.svrMimo.epsilon') : t('tooltips.models.svrDir.epsilon')} width="w-72" />
        </div>
        <input
          id="model-config-EPSILON"
          type="number"
          value={modelParams.EPSILON || ''}
          onChange={(e) => {
            const val = e.target.value;
            onParameterChange('EPSILON', val ? parseFloat(val) : undefined);
          }}
          disabled={disabled}
          className={inputBase}
          min="0.001"
          max="1"
          step="0.01"
          placeholder={t('training.modelConfiguration.placeholders.enterValue')}
        />
      </div>
    </>
  );
});

SVRParams.displayName = 'SVRParams';

export default SVRParams;
